import { useNavigate } from "react-router-dom";
import {
  Tag,
  Layers,
  Info,
  Building2,
  Calendar,
  AlertCircle,
  ArrowRight,
} from "lucide-react";
import {
  DetailRow,
  DetailGrid,
  SectionHeading,
} from "@/components/dashboard/dashboardDetailPrimitives";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { getIdentificareStatus } from "@/utils/guideProgressCalculator";
import { resolveServiceAnnex } from "@/utils/sectorUtils";
import { NOTIFICARE_SECTION_LABELS } from "@/constants/notificareSectionLabels";
import type { EntityData } from "@/types/entity";
import type { Sector } from "@/types/risk-analysis";

type NotificareDetailsProps = {
  entityData: EntityData;
  sectors: Sector[];
  onDetailsClick?: () => void;
  notificationChecks: Record<string, boolean>;
};

const STATUS_LABELS: Record<string, string> = {
  completed: "COMPLETAT",
  in_progress: "IN LUCRU",
  not_started: "NEINCEPUT",
};

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
}

export default function NotificareDetails({
  entityData,
  sectors,
  onDetailsClick,
  notificationChecks,
}: NotificareDetailsProps) {
  const navigate = useNavigate();
  const identStatus = getIdentificareStatus(entityData);
  const classification = entityData.nis_classification?.classificationV2;
  const isArt9Applicable =
    classification === "IMPACT" || entityData.impact_analysis !== null;
  const services = entityData.selected_services ?? [];
  const servicesWithAnnex = resolveServiceAnnex(services, sectors);

  const missingSections = Object.entries(notificationChecks)
    .filter(([, complete]) => !complete)
    .map(([key]) => key);

  return (
    <div className="space-y-2">
      {missingSections.length > 0 && (
        <Alert className="mb-3 border-amber-200 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-600 mt-1.5" />
          <AlertTitle className="text-amber-800 text-sm">
            <div className="flex items-center justify-between gap-2">
              <p className="text-amber-800 text-sm">Secțiuni incomplete</p>
              <Button
                variant="ghost"
                size="xs"
                className="gap-1.5 text-amber-700 hover:text-amber-900 hover:bg-amber-100"
                onClick={() =>
                  navigate(
                    `/onboarding/formular-notificare?section=${missingSections[0]}`
                  )
                }
              >
                Completează
                <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </AlertTitle>
          <AlertDescription>
            <ul className="mt-1.5 space-y-0.5">
              {missingSections.map((sectionId) => (
                <li
                  key={sectionId}
                  className="flex items-center gap-2 text-xs text-amber-700"
                >
                  <span className="w-1 h-1 rounded-full bg-amber-400 shrink-0" />
                  {NOTIFICARE_SECTION_LABELS[sectionId] ?? sectionId}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <SectionHeading
        onClick={onDetailsClick}
        to={onDetailsClick ? undefined : "/identificare-raportare"}
      >
        Informatii Formular
      </SectionHeading>
      <DetailGrid>
        <DetailRow
          icon={Info}
          label="Status"
          value={STATUS_LABELS[identStatus] ?? identStatus}
          highlighted
        />
        <DetailRow
          icon={Tag}
          label="Incadrare"
          value={classification ?? "-"}
          highlighted
        />
        <DetailRow
          icon={Layers}
          label="Art. 9"
          value={isArt9Applicable ? "DA" : "NU"}
        />
        <DetailRow
          icon={Building2}
          label="Dimensiune"
          value={entityData.dimensiune ?? "-"}
        />
        <DetailRow
          icon={Calendar}
          label="Data transmitere"
          value={
            entityData.created_at ? formatDate(entityData.created_at) : "-"
          }
        />
      </DetailGrid>

      {services.length > 0 && (
        <>
          <SectionHeading>Servicii Notificate</SectionHeading>
          <div className="rounded-lg border border-slate-200 overflow-hidden shadow-sm">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gradient-to-r from-slate-50 to-slate-100">
                  <th className="text-left px-3 py-2.5 font-bold text-slate-700 text-[11px] uppercase tracking-wide">
                    Anexa
                  </th>
                  <th className="text-left px-3 py-2.5 font-bold text-slate-700 text-[11px] uppercase tracking-wide">
                    Sector
                  </th>
                  <th className="text-left px-3 py-2.5 font-bold text-slate-700 text-[11px] uppercase tracking-wide">
                    Serviciu
                  </th>
                </tr>
              </thead>
              <tbody>
                {servicesWithAnnex.map((service, idx) => (
                  <tr
                    key={`${service.category_id}-${idx}`}
                    className="border-t border-slate-100 even:bg-slate-50/50 hover:bg-blue-50/30 transition-colors"
                  >
                    <td className="px-3 py-2.5 text-slate-600">
                      {service.annex ? `Anexa ${service.annex}` : "-"}
                    </td>
                    <td className="px-3 py-2.5 text-slate-700 font-medium">
                      {service.sector_name}
                    </td>
                    <td className="px-3 py-2.5 text-slate-600">
                      {service.name}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
